import { CHANNELS, TIME_SINKS, SYSTEMS, GOALS, industryById } from "@/lib/discovery";

/**
 * The brief a visitor builds by answering the opportunity finder, in a shape
 * every booking route can carry: the WhatsApp message, the Cal.com notes
 * prefill and the callback form all read from here, so the call always starts
 * with us already knowing the business.
 *
 * Stored in sessionStorage rather than the URL: it is the visitor's own answers,
 * it should not be shareable or land in a server log, and it should not outlive
 * the visit.
 */
const KEY = "autlabsBrief";
const MAX = 1200;

const labelsFor = (list, ids) =>
  (ids || []).map((id) => list.find((x) => x.id === id)?.label).filter(Boolean);

/** Builds the brief from finished diagnostic answers and their scored opportunities. */
export function buildBrief(answers, opportunities = []) {
  const industry = industryById(answers.industry);
  const channels = labelsFor(CHANNELS, answers.channels);
  const timeSinks = labelsFor(TIME_SINKS, answers.timeSinks);
  const systems = labelsFor(SYSTEMS, answers.systems);
  const goal = GOALS.find((g) => g.id === answers.goal)?.label || "";
  const top = opportunities.slice(0, 3).map((o) => ({ problem: o.problem, impact: o.impact }));

  const said = (list) => (list.length ? list.join(", ") : "not answered");
  const facts = [
    ["Business", industry.label],
    ["Customers reach them by", said(channels)],
    ["Time goes on", said(timeSinks)],
    ["Information lives in", said(systems)],
    ["Wants to", goal || "not answered"],
  ];

  const text = [
    "Here is what I filled in on your opportunity finder:",
    "",
    ...facts.map(([k, v]) => `${k}: ${v}`),
    "",
    top.length ? "Top opportunities it showed me:" : "",
    ...top.map((o, i) => `${i + 1}. ${o.problem} (${o.impact})`),
    "",
    "I would like to go through the full roadmap on the call.",
  ]
    .filter((line, i, all) => !(line === "" && all[i - 1] === ""))
    .join("\n")
    .slice(0, MAX);

  return { industry: industry.label, goal, facts, top, text };
}

/** Saves the brief for the booking pages. Storage can throw in private mode. */
export function saveBrief(brief) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(brief));
  } catch {
    // A brief that cannot be stored is not worth failing the results page for.
  }
}

/** Returns the stored brief, or null when there is none or it is unreadable. */
export function loadBrief() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const brief = JSON.parse(raw);
    return brief && brief.text ? brief : null;
  } catch {
    return null;
  }
}

export function clearBrief() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: the brief simply stays for the rest of the session.
  }
}

/** Cal.com prefills its "additional notes" field from the notes query param. */
export function calUrlWithBrief(calEmbed, brief) {
  if (!brief?.text) return calEmbed;
  const sep = calEmbed.includes("?") ? "&" : "?";
  return `${calEmbed}${sep}notes=${encodeURIComponent(brief.text)}`;
}
